import { EventType } from "@/analytics/analytics-events";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert, GUID } from "@faro-lotv/foundation";
import { IElementRoomSection } from "@faro-lotv/ielement-types";
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from "react";
import { Vector3 } from "three";
import { WaypointNameEditor } from "../sheet-context-menu/new-waypoints/new-waypoint-name-editor";
import { useEditWaypointsStore } from "./edit-waypoints-store";

type LocationMarkerNameEditorProp = {
  /** The parent that the editor should have in the html DOM */
  parentRef: MutableRefObject<HTMLElement>;

  /** The new location marker to edit name */
  newLocationMarker: IElementRoomSection;

  /** True if it is editing right after location marker creation */
  editingRightAfterCreation: boolean;

  /** The callback function to exit the name editor */
  onExit(): void;
};

type WaypointToEdit = {
  /** The id of the waypoint being edited.  */
  id: GUID;

  /** Whether the waypoint is being edited right after creation. */
  editingRightAfterCreation: boolean;
};

/**
 * @returns in-place location marker name editor
 */
export function LocationMarkerNameEditor({
  parentRef,
  newLocationMarker,
  editingRightAfterCreation,
  onExit,
}: LocationMarkerNameEditorProp): JSX.Element {
  const anchorPoint = useMemo(() => {
    assert(newLocationMarker.pose?.pos);
    return new Vector3(
      newLocationMarker.pose.pos.x,
      newLocationMarker.pose.pos.y,
      newLocationMarker.pose.pos.z,
    );
  }, [newLocationMarker]);

  const { newLocationMarkers, updateNewLocationMarkers } =
    useEditWaypointsStore();

  const [waypointToEdit, setWaypointToEdit] = useState<WaypointToEdit>();
  useEffect(() => {
    setWaypointToEdit({
      id: newLocationMarker.id,
      editingRightAfterCreation,
    });
  }, [editingRightAfterCreation, newLocationMarker.id]);

  const applyNameChange = useCallback(
    ({ id, editingRightAfterCreation }: WaypointToEdit, newName: string) => {
      const locationMarker = newLocationMarkers.find(
        (marker) => marker.id === id,
      );
      assert(locationMarker, "Location marker to rename not found");

      const name = newName.trim();
      if (name.length && name !== locationMarker.name) {
        Analytics.track(
          editingRightAfterCreation
            ? EventType.setLocationMarkerName
            : EventType.editLocationMarkerName,
        );
        const renamedLocationMarker = {
          ...locationMarker,
          name,
        };
        const updatedLocationMarkers = newLocationMarkers.filter(
          (marker) => marker.id !== locationMarker.id,
        );
        updatedLocationMarkers.push(renamedLocationMarker);
        updateNewLocationMarkers(updatedLocationMarkers);
      }
    },
    [newLocationMarkers, updateNewLocationMarkers],
  );

  return (
    <WaypointNameEditor
      parentRef={parentRef}
      anchorPoint={anchorPoint}
      waypointInfo={waypointToEdit}
      initialName={newLocationMarker.name}
      onApplyName={applyNameChange}
      onExit={onExit}
    />
  );
}
